import { sanitizeAiMarkdownHtml } from './html-sanitizer';

/**
 * Minimal Markdown -> HTML renderer for AI assistant replies.
 *
 * The Pebbl chat endpoint returns its answers as Markdown, and in practice that means tables
 * (a "what quotes do we have" question comes back as a 17-row table of links), links, bullet
 * lists, headings and bold runs. Binding that as plain text renders raw pipes and brackets, so
 * it has to be converted.
 *
 * WHY HAND-WRITTEN
 * ----------------
 * The alternative is a Markdown library (marked/markdown-it), which is a new runtime dependency
 * and a chunk of bundle for one widget. This covers the subset the agent actually emits; extend
 * it here rather than reaching for a library for a single missing construct.
 *
 * SAFETY
 * ------
 * The input is escaped **before** any conversion, so every tag in the output is one this file
 * produced - inline HTML in the model's answer is inert text, not markup. Link targets are
 * checked against a protocol allow-list (no `javascript:`), and the result is passed through
 * DOMPurify. Callers bind the returned string with `[innerHTML]`, which puts Angular's own
 * sanitizer in front of the DOM as a third layer.
 *
 * DOMPurify needs a DOM, so on the server the escaped text is returned unconverted. In practice
 * this never runs there - the chat surfaces are browser-only.
 */
export function renderMarkdown(markdown: string | null | undefined): string {
  const source = (markdown ?? '').replace(/\r\n/g, '\n');
  if (!source.trim()) {
    return '';
  }

  // Escape first: everything below emits tags, and nothing from the model may.
  const escaped = escapeHtml(source);

  // Park code spans/blocks so their contents survive the inline formatting pass verbatim.
  const codeBlocks: string[] = [];
  const withoutCode = escaped
    .replace(/```([a-zA-Z0-9+#-]*)\n([\s\S]*?)```/g, (_match, _language, code: string) =>
      storeToken(codeBlocks, `<pre class="md-code"><code>${code.replace(/\n$/, '')}</code></pre>`))
    .replace(/`([^`\n]+)`/g, (_match, code: string) =>
      storeToken(codeBlocks, `<code class="md-inline-code">${code}</code>`));

  const html = renderBlocks(withoutCode.split('\n'));

  // Put the code back, then sanitize the finished document.
  const restored = html.replace(CODE_TOKEN_PATTERN, (_match, index: string) => codeBlocks[Number(index)]);

  return typeof window === 'undefined' ? restored : sanitizeAiMarkdownHtml(restored);
}

/**
 * Walks the document line by line, closing the current block whenever the line type changes.
 */
function renderBlocks(lines: string[]): string {
  const html: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    // Blank line: nothing to emit, blocks are closed by their own readers.
    if (!line.trim()) {
      index++;
      continue;
    }

    // A parked code block stands alone - never wrapped in the <p> below, which would be
    // invalid markup around a <pre>.
    const codeToken = /^\s*(%%CODE\d+%%)\s*$/.exec(line);
    if (codeToken) {
      html.push(codeToken[1]);
      index++;
      continue;
    }

    // Horizontal rule.
    if (/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      html.push('<hr>');
      index++;
      continue;
    }

    // Heading. The chat panel is small, so h1/h2 in a reply are rendered at h3 and below by CSS.
    const heading = /^\s{0,3}(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      html.push(`<h${level}>${renderInline(heading[2].trim())}</h${level}>`);
      index++;
      continue;
    }

    // Table: any row of cells immediately followed by a |---|---| delimiter row.
    if (isTableStart(lines, index)) {
      index = readTable(lines, index, html);
      continue;
    }

    // Blockquote.
    if (BLOCKQUOTE_PATTERN.test(line)) {
      const quoted: string[] = [];
      while (index < lines.length && BLOCKQUOTE_PATTERN.test(lines[index])) {
        quoted.push(lines[index].replace(BLOCKQUOTE_PATTERN, ''));
        index++;
      }
      html.push(`<blockquote>${renderBlocks(quoted)}</blockquote>`);
      continue;
    }

    // List (bulleted or numbered, nested by indentation).
    if (isListItem(line)) {
      index = readList(lines, index, html);
      continue;
    }

    // Paragraph: run to the next blank line or block start. Single newlines inside a paragraph
    // become <br> - the agent streams a "line" at a time and means them literally.
    const paragraph: string[] = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !isListItem(lines[index]) &&
      !isPipeRow(lines[index]) &&
      !isTableStart(lines, index) &&
      !/^\s*%%CODE\d+%%\s*$/.test(lines[index]) &&
      !BLOCKQUOTE_PATTERN.test(lines[index]) &&
      !/^\s{0,3}(#{1,6}\s|(-{3,}|\*{3,}|_{3,})\s*$)/.test(lines[index])
    ) {
      paragraph.push(lines[index].trim());
      index++;
    }
    // A pipe row with no |---| row under it is not a table, and the loop above stops at pipe
    // rows so a paragraph never runs into one. Left there, it took no lines, `index` never moved
    // and this walk spun on the same line for ever - freezing the tab. Mid-stream that is any
    // table header whose delimiter row has not arrived yet. It is text: take it as such.
    if (!paragraph.length) {
      paragraph.push(lines[index].trim());
      index++;
    }
    html.push(renderParagraph(paragraph));
  }

  return html.join('');
}

/**
 * Renders one paragraph, promoting a run of `**Label:** value` lines to a two-column list.
 *
 * A totals block - "Subtotal (ex GST): $501.10" and the two lines under it - arrives as bold
 * labels separated by single newlines. As a <p> of <br>s each value starts wherever its own
 * label happened to end, so the column of figures reads as a ragged stagger. A <dl> lets CSS
 * put every value on one left edge.
 *
 * Only a *whole* paragraph converts, and only from two lines up: a single "**Note:** ..." line
 * is a sentence, not a table, and has nothing to align against. Mid-stream the last line is
 * still partial and won't match, so the block renders as a paragraph until the run completes.
 */
function renderParagraph(lines: string[]): string {
  const labelled = lines.map(readLabelledLine);
  if (lines.length < 2 || labelled.some((line) => line === null)) {
    return `<p>${lines.map(renderInline).join('<br>')}</p>`;
  }

  const rows = (labelled as LabelledLine[]).map((line) => {
    const emphasis = line.isEmphasised ? ' class="md-kv-strong"' : '';
    return `<dt${emphasis}>${renderInline(line.label)}</dt><dd${emphasis}>${renderInline(line.value)}</dd>`;
  });

  return `<dl class="md-kv">${rows.join('')}</dl>`;
}

interface LabelledLine {
  label: string;
  value: string;
  isEmphasised: boolean;
}

/**
 * Reads `**Label:** value`, and the whole-line-bold `**Label: value**` the agent uses for the
 * figure it wants to stand out (the final total). The colon has to sit inside the bold run, so
 * an ordinary bolded phrase mid-sentence is not mistaken for a label.
 *
 * The label length is capped: past that it is a bolded sentence that happens to contain a
 * colon, and letting it set a `max-content` column would squeeze every value off the page.
 */
function readLabelledLine(line: string): LabelledLine | null {
  // A bold link - `**[Name](https://...)**`, a product entry's name - is not a label: the colon
  // the pattern would find is the URL's own.
  if (/^\*\*\s*\[/.test(line)) {
    return null;
  }

  const split = /^\*\*\s*([^*:]+?)\s*:\s*\*\*\s*(.+)$/.exec(line);
  if (split && split[1].length <= MAX_LABEL_LENGTH) {
    return { label: `${split[1]}:`, value: split[2].trim(), isEmphasised: false };
  }

  const whole = /^\*\*\s*([^*:]+?)\s*:\s*([^*]+?)\s*\*\*$/.exec(line);
  if (whole && whole[1].length <= MAX_LABEL_LENGTH) {
    return { label: `${whole[1]}:`, value: whole[2], isEmphasised: true };
  }

  return null;
}

/** Longest bold run still read as a label rather than a bolded sentence. */
const MAX_LABEL_LENGTH = 40;

/**
 * Reads a pipe table into <table>/<thead>/<tbody>/<tr>/<th>/<td> markup, honouring the
 * delimiter row's alignment markers (`---:` right, `:---:` centre) - totals columns come back
 * right-aligned - and right-aligning an unmarked column of figures (see the alignment pass).
 *
 * The header sets the column count and EVERY row is squared off to it (see `padRow`). A `<tr>`
 * with the wrong number of `<td>`s is not a styling problem to be patched downstream: a short
 * row simply ends, so the browser leaves the tail of that row empty and a long one grows a
 * phantom column that shifts the headers off their own data for the whole table.
 *
 * @returns the index of the first line after the table
 */
function readTable(lines: string[], start: number, html: string[]): number {
  const headerCells = splitTableRow(lines[start]);
  const columnCount = headerCells.length;
  const delimiters = padRow(splitTableRow(lines[start + 1]), columnCount);
  const authored = delimiters.map(readAlignment);
  let index = start + 2;

  // Read the body first: the alignment markers are checked against the data below before a
  // single cell is emitted, so the rows have to be in hand.
  const rows: string[][] = [];
  while (index < lines.length && isTableBodyRow(lines[index])) {
    const cells = padRow(splitTableRow(lines[index]), columnCount);
    index++;

    // A row the model padded out to keep its own source grid square ("| | | |") carries
    // nothing. Rendered it is a blank stripe across the table with no data behind it.
    if (cells.every((cell) => !cell)) {
      continue;
    }

    rows.push(cells);
  }

  // The marker is checked against the data both ways. A `---:` over specs is demoted to left,
  // and an UNMARKED column (`---`, no colon either side) that holds nothing but figures is
  // promoted to right - the agent routinely sends its price columns unmarked, and left-aligned
  // the decimal points of "$95.00" and "$120.00" do not line up. An explicit `:---` is the
  // agent asking for left and is kept.
  const alignments = authored.map((alignment, column) => {
    if (alignment === 'right' && !isFigureColumn(rows, column)) {
      return 'left';
    }
    if (!delimiters[column].includes(':') && hasFigures(rows, column) && isFigureColumn(rows, column)) {
      return 'right';
    }
    return alignment;
  });

  const bodyRows = rows.map((cells) =>
    `<tr>${cells.map((cell, cellIndex) =>
      `<td${alignmentAttribute(alignments[cellIndex])}>${renderInline(cell)}</td>`).join('')}</tr>`);

  html.push(
    '<div class="md-table-scroll"><table class="md-table">' +
    `<thead><tr>${headerCells.map((cell, cellIndex) =>
      `<th${alignmentAttribute(alignments[cellIndex])}>${renderInline(cell)}</th>`).join('')}</tr></thead>` +
    `<tbody>${bodyRows.join('')}</tbody>` +
    '</table></div>'
  );

  return index;
}

/**
 * Squares one row off against the header's column count.
 *
 * Short rows are padded with empty cells. Long rows fold their surplus back into the last
 * column rather than being cut: a row with too many cells is almost always an unescaped `|`
 * inside a value, and dropping the tail would lose the figure the answer was asked for.
 */
function padRow(cells: string[], columnCount: number): string[] {
  if (cells.length === columnCount) {
    return cells;
  }

  if (cells.length < columnCount) {
    return cells.concat(new Array(columnCount - cells.length).fill(''));
  }

  const kept = cells.slice(0, columnCount - 1);
  kept.push(cells.slice(columnCount - 1).join(' | '));
  return kept;
}

/**
 * Whether a column the agent marked `---:` actually holds figures.
 *
 * The marker is a HINT, and not a reliable one: the agent right-aligns any column whose values
 * begin with a digit, so a dimensions column ("300 x 50 x 20 mm") comes back right-aligned
 * alongside the prices. Right-alignment earns its place on a column of figures, where it puts
 * the decimal points under one another and lets a customer scan a total. On a column of specs
 * it does the opposite - it pushes every value away from the heading it belongs to and opens a
 * gutter down the middle of the table, which is what the dimensions column looked like.
 *
 * So the hint is kept only where the column's own data backs it up. Vacuously true while the
 * body is still empty: mid-stream the header arrives before its rows, and trusting the marker
 * until something contradicts it keeps a genuine totals column from flicking left and back as
 * the first row lands.
 */
function isFigureColumn(rows: string[][], column: number): boolean {
  return rows.every((row) => {
    const value = stripEmphasis(row[column] ?? '');
    return !value || FIGURE_PATTERN.test(value);
  });
}

/**
 * A figure and nothing else: an optional sign or accounting bracket, an optional currency mark,
 * digits with thousands separators, an optional decimal, an optional percent.
 *
 * Deliberately strict about trailing text. "300 x 50 x 20 mm" and "lineal metre" are specs, not
 * quantities, and a unit suffix is the thing that most often tells them apart from one.
 */
const FIGURE_PATTERN = /^[-+(]?\s*[$€£¥]?\s*\d[\d,\s]*(?:\.\d+)?\s*%?\)?$/;

/**
 * True where the column holds at least one value. isFigureColumn() is vacuously true on an empty
 * column, which is right for trusting an explicit marker mid-stream but would promote an
 * unmarked column before a single row has arrived.
 */
function hasFigures(rows: string[][], column: number): boolean {
  return rows.some((row) => !!stripEmphasis(row[column] ?? ''));
}

/** Drops the inline markers, so `**$36,910.50**` is still read as the figure it is. */
function stripEmphasis(value: string): string {
  return value.replace(/[*_`~]/g, '').trim();
}

/**
 * Reads one list and any lists nested under it.
 * @returns the index of the first line after the list
 */
function readList(lines: string[], start: number, html: string[]): number {
  const first = parseListItem(lines[start]) as ParsedListItem;
  const tag = first.isOrdered ? 'ol' : 'ul';
  const items: string[] = [];
  let index = start;

  while (index < lines.length) {
    const item = parseListItem(lines[index]);

    // A deeper marker belongs to the item just emitted, as a nested list *inside* its <li> -
    // appending after the </li> would render the sub-list as a sibling at the outer level.
    if (item && item.indent > first.indent && items.length) {
      const nested: string[] = [];
      const end = readList(lines, index, nested);
      items[items.length - 1] = items[items.length - 1]
        .replace(/<\/li>$/, `${nested.join('')}</li>`);
      index = end;
      continue;
    }

    // A shallower marker closes this list and resumes in the caller.
    if (item && item.indent < first.indent) {
      break;
    }

    // Switching between bulleted and numbered at the same level starts a new list, rather than
    // silently folding numbered steps into the bullets above them.
    if (item && item.indent === first.indent && item.isOrdered !== first.isOrdered) {
      break;
    }

    if (item) {
      items.push(`<li>${renderInline(item.text)}</li>`);
      index++;
      continue;
    }

    // A plain indented line continues the previous item; anything else ends the list.
    if (lines[index].trim() && /^\s{2,}/.test(lines[index]) && items.length) {
      items[items.length - 1] = items[items.length - 1].replace(
        /<\/li>$/, `<br>${renderInline(lines[index].trim())}</li>`
      );
      index++;
      continue;
    }

    break;
  }

  html.push(`<${tag}>${linkItemNames(items).join('')}</${tag}>`);
  return index;
}

/**
 * Links the bold name a list item opens on - `**MAPLE MOON Coping**, 600 x 400 x 28...` - to a
 * product search for it, so it reads and works like the linked names beside it.
 *
 * The agent links a product where it has its page and bolds the name where it has none, so one
 * list mixed a green link with black bold names that went nowhere. The search is the honest
 * target: it finds the product without guessing at a page URL.
 *
 * Only a name: a bold run with a colon is a label ("**Note:** ..."), and a bold run with bullets
 * nested straight under it ("**Important considerations before specifying**") is a section
 * heading. Otherwise it counts where a sibling item opens on a link (the mixed list above), or
 * where the name is followed by a comma, full stop or dash - a bold word opening a sentence
 * ("**Always** seal...") is neither.
 */
function linkItemNames(items: string[]): string[] {
  const hasLinkedName = items.some((item) => LINKED_NAME_ITEM.test(item));
  return items.map((item) => item.replace(BOLD_NAME_ITEM, (match, name: string, _offset: number, whole: string) => {
    const rest = whole.slice(match.length);
    if (SECTION_FOLLOWER.test(rest)) {
      return match;
    }
    return hasLinkedName || NAME_FOLLOWER.test(rest)
      ? `<li><strong>${productSearchLink(unescapeHtml(name))}</strong>`
      : match;
  }));
}

/** An item opening on a link, bare or in bold/italic. */
const LINKED_NAME_ITEM = /^<li>(?:<(?:strong|em)>)*<a\s/;
/** An item opening on a bold run with no markup or colon inside it. */
const BOLD_NAME_ITEM = /^<li><strong>([^<:]+)<\/strong>/;
/** What follows a product name rather than a bolded word: a comma, full stop or dash. */
const NAME_FOLLOWER = /^(?:[,.]|\s+[-\u2013\u2014]\s|<\/li>|<br>)/;
/** Bullets nested straight under the bold run - a section heading, not a product name. */
const SECTION_FOLLOWER = /^\s*<(?:ul|ol)>/;

/**
 * A reply link to the product search for `name` (plain text), drawn as every reply link is.
 * Marked `md-search-link` so the product entries never take it for a product page link.
 * Root-relative, so it opens the search on whichever site the chat is on.
 */
export function productSearchLink(name: string): string {
  const href = escapeHtml(`/search?q=${encodeURIComponent(name.trim())}`);
  return `<a class="md-search-link" href="${href}" target="_blank" rel="noopener noreferrer">` +
    `${withLinkArrow(escapeHtml(name.trim()))}</a>`;
}

function unescapeHtml(value: string): string {
  return value.replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

/**
 * Applies the inline constructs: bold, italic, strikethrough and links.
 */
function renderInline(text: string): string {
  return text
    .replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*\w])\*([^*\n]+)\*(?!\w)/g, '$1<em>$2</em>')
    .replace(/(^|[^_\w])_([^_\n]+)_(?!\w)/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<del>$1</del>')
    .replace(/\[([^\]]*)\]\(([^)\s]+)(?:\s+&quot;[^)]*&quot;)?\)/g, (match, label: string, url: string) => {
      const href = safeHref(url);
      // An unusable target (javascript:, data:, a bare word) degrades to its label rather than
      // rendering a link that cannot be trusted.
      return href
        ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${withLinkArrow(label || href)}</a>`
        : match;
    });
}

/**
 * Appends the ↗ that marks a reply link as opening in a new tab. The glyph itself is drawn by
 * CSS (`.md-link-arrow`); this only places the empty span it hangs off.
 *
 * The arrow is tied to the label's last word with a nowrap span. Left loose it is a line-break
 * opportunity of its own, and at some panel widths it wrapped onto a line by itself, under a
 * link it no longer sat beside. A last word that is long (a bare URL as the label) or ends in
 * markup is left out of the span: nowrap there would stop the link wrapping at all.
 */
function withLinkArrow(label: string): string {
  const arrow = '<span class="md-link-arrow" aria-hidden="true"></span>';
  const lastWord = /([^\s<>]+)$/.exec(label);
  if (!lastWord || lastWord[1].length > MAX_LINK_TAIL_LENGTH) {
    return `${label}${arrow}`;
  }
  return `${label.slice(0, lastWord.index)}<span class="md-link-tail">${lastWord[1]}${arrow}</span>`;
}

/** Longest last word kept on one line with the link arrow. */
const MAX_LINK_TAIL_LENGTH = 24;

interface ParsedListItem {
  indent: number;
  isOrdered: boolean;
  text: string;
}

function parseListItem(line: string): ParsedListItem | null {
  const match = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(line ?? '');
  if (!match) {
    return null;
  }
  return { indent: match[1].length, isOrdered: !match[2], text: match[4].trim() };
}

function isListItem(line: string): boolean {
  return parseListItem(line) !== null;
}

/**
 * True where a table begins: a row of cells with a |---|---| delimiter directly under it.
 *
 * The OUTER PIPES ARE OPTIONAL, which is the point of doing this properly. The agent emits
 * both forms - `| Product | Finish |` and the bare `Product | Finish` - and the bare one used
 * to fail the old leading-pipe test, so the whole table fell through to the paragraph reader
 * and rendered as a wall of text with the pipes still in it. Two shapes of answer to the same
 * question is exactly the inconsistency this file exists to remove.
 *
 * The delimiter must itself contain a separator. Without that, a heading followed by a `---`
 * underline would read as a one-column table instead of a horizontal rule.
 */
function isTableStart(lines: string[], index: number): boolean {
  const header = lines[index] ?? '';
  const delimiter = lines[index + 1] ?? '';
  if (!header.trim() || !containsSeparator(header) || !containsSeparator(delimiter)) {
    return false;
  }

  const cells = splitTableRow(delimiter);
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell));
}

/** A line still belonging to the table being read - anything without a cell break ends it. */
function isTableBodyRow(line: string): boolean {
  return !!(line ?? '').trim() && containsSeparator(line);
}

/**
 * A `|` that is not backslash-escaped, i.e. a real cell break.
 *
 * Written as "a start-of-line or non-backslash, then a pipe" rather than as a lookbehind:
 * lookbehind is still the newest part of the regex engine and this runs on every line of every
 * streaming frame.
 */
function containsSeparator(line: string): boolean {
  return /(^|[^\\])\|/.test(line ?? '');
}

/**
 * A fully pipe-delimited row. Narrower than `isTableStart` and used only to close a paragraph,
 * so a stray `|` mid-sentence never splits one.
 */
function isPipeRow(line: string): boolean {
  return /^\s{0,3}\|.*\|\s*$/.test(line ?? '');
}

/**
 * Splits one row into its cells on the unescaped `|`.
 *
 * `\|` inside a value is a literal pipe, not a break - the agent escapes them in dimensions
 * and finish notes ("600 x 300 \| Honed"). Scanned a character at a time because the split has
 * to know about the escape, and the same pass is what unescapes it.
 *
 * The optional outer pipes are stripped up front rather than by dropping empty end cells: a
 * genuinely empty first column ("|  | Total |") is data, and trimming it would shift every
 * cell in the row one column to the left.
 */
function splitTableRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) {
    row = row.slice(1);
  }
  if (row.endsWith('|') && !row.endsWith('\\|')) {
    row = row.slice(0, -1);
  }

  const cells: string[] = [];
  let cell = '';
  for (let index = 0; index < row.length; index++) {
    if (row[index] === '\\' && row[index + 1] === '|') {
      cell += '|';
      index++;
      continue;
    }
    if (row[index] === '|') {
      cells.push(cell.trim());
      cell = '';
      continue;
    }
    cell += row[index];
  }
  cells.push(cell.trim());

  return cells;
}

function readAlignment(cell: string): 'left' | 'center' | 'right' {
  const startsWithColon = cell.startsWith(':');
  const endsWithColon = cell.endsWith(':');
  if (startsWithColon && endsWithColon) return 'center';
  if (endsWithColon) return 'right';
  return 'left';
}

/**
 * Alignment is emitted as a class, not a style attribute - DOMPurify and Angular's sanitizer
 * both strip inline styles.
 */
function alignmentAttribute(alignment: 'left' | 'center' | 'right' | undefined): string {
  return alignment && alignment !== 'left' ? ` class="md-align-${alignment}"` : '';
}

/**
 * Allows only navigable protocols. The input is already HTML-escaped, so `&amp;` is unwrapped
 * for the protocol test and the escaped form is what ends up in the attribute.
 */
function safeHref(url: string): string | null {
  const candidate = url.trim().replace(/&amp;/g, '&');
  return /^(https?:\/\/|mailto:|tel:|\/|#)/i.test(candidate) ? url.trim() : null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Placeholder for parked code, matched on restore. `%%` carries no meaning in Markdown. */
const CODE_TOKEN_PATTERN = /%%CODE(\d+)%%/g;

/**
 * Blockquote marker. Matched as `&gt;` rather than `>` because the source is HTML-escaped
 * before any block is parsed.
 */
const BLOCKQUOTE_PATTERN = /^\s{0,3}&gt;\s?/;

/**
 * Parks a rendered fragment behind a placeholder that no Markdown rule can match.
 */
function storeToken(store: string[], html: string): string {
  store.push(html);
  return `%%CODE${store.length - 1}%%`;
}
